/**
 * Dashboard - Primary entry point and campaign management hub
 * 
 * Features:
 * - Hero section with entity counts and CTA
 * - Recent campaigns grid
 * - Empty state handling
 * - Navigation to create new campaign (Step 1)
 * 
 * Integration: Fetches from GET /api/v1/campaigns/recent
 * If API unavailable, shows empty state with mock data fallback
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroCTA from '../components/dashboard/HeroCTA';
import RecentCampaignCard from '../components/dashboard/RecentCampaignCard';
import { AlertCircle, ArrowRight, Database, LayoutGrid, Plus, Sparkles } from 'lucide-react';
import { Button } from '../design-system/components/Button/Button';
import { setEmmaPageState } from '../utils/emmaPageState';
import { apiFetch } from '@/lib/api';
import CreditBalanceWidget from '../components/credits/CreditBalanceWidget';
import PartnerUsageDashboard from '../components/credits/PartnerUsageDashboard';
import { hasPermission } from '../types/roles';
import { useAuth } from '../contexts/AuthContext';
import campaignService from '@/services/api/campaigns/campaignService';

interface Campaign {
  id: string;
  name: string;
  clientName: string;
  status: 'draft' | 'active' | 'completed' | 'archived' | 'scheduled';
  createdAt: string;
  templateType?: 'email' | 'landing_page' | 'social' | 'content';
}

type CreatedDateRange = 'all' | 'today' | 'week' | 'month';

const MS_DAY = 24 * 60 * 60 * 1000;

function startOfLocalDayMs(d = new Date()): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function dashboardCampaignMatchesCreated(c: Campaign, range: CreatedDateRange): boolean {
  if (range === 'all') return true;
  const t = new Date(c.createdAt).getTime();
  const now = Date.now();
  if (range === 'today') return t >= startOfLocalDayMs();
  if (range === 'week') return t >= now - 7 * MS_DAY;
  if (range === 'month') return t >= now - 30 * MS_DAY;
  return true;
}

interface EntityCounts {
  clients: number;
  partners: number;
  vendors: number;
  solutions: number;
  icps: number;
  documents: number;
  documents_processing: number;
  last_updated: string | null;
  warnings: Array<{ type: string; message: string; link?: string }>;
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Note: useEmmaContext hook removed - using global store (setEmmaPageState) instead
  // The hook's updatePageState was causing re-render loops due to function identity changes
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [counts, setCounts] = useState<EntityCounts>({
    clients: 0,
    partners: 0,
    vendors: 0,
    solutions: 0,
    icps: 0,
    documents: 0,
    documents_processing: 0,
    last_updated: null,
    warnings: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createdDateRange, setCreatedDateRange] = useState<CreatedDateRange>('all');
  const [activationOptions, setActivationOptions] = useState<Record<string, any> | null>(null);

  const filteredDashboardCampaigns = useMemo(() => {
    return campaigns.filter((c) => dashboardCampaignMatchesCreated(c, createdDateRange));
  }, [campaigns, createdDateRange]);

  // Fetch recent campaigns and entity counts
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Recent campaigns — apiFetch adds Bearer + X-Organization-ID (matches backend org scope)
        try {
          const campaignData = await apiFetch('/api/v1/campaigns/recent');
          const normalized = Array.isArray(campaignData)
            ? campaignData.map((c: any) => ({
                id: String(c.id ?? c.campaign_id ?? ''),
                name: c.name,
                status: (c.status || 'draft').toLowerCase() as Campaign['status'],
                clientName: c.client_name || c.clientName || '—',
                createdAt: c.created_at || c.createdAt || new Date().toISOString(),
                templateType: c.campaign_type || c.templateType,
              })).filter((c: Campaign) => Boolean(c.id))
            : [];
          setCampaigns(normalized);
        } catch {
          // Fallback: empty list (no mock spam when API/auth unavailable)
          setCampaigns([]);
        }

        const [countsData, activationData] = await Promise.all([
          apiFetch('/api/v1/ecosystem/counts').catch(() => null),
          apiFetch('/api/v1/activation/options').catch(() => null),
        ]);
        if (countsData) {
          setCounts({
            clients: countsData.clients || 0,
            partners: countsData.partners || 0,
            vendors: countsData.vendors || 0,
            solutions: countsData.solutions || 0,
            icps: countsData.icps || 0,
            documents: countsData.documents || 0,
            documents_processing: countsData.documents_processing || 0,
            last_updated: countsData.last_updated || null,
            warnings: countsData.warnings || [],
          });
        }
        setActivationOptions(activationData || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        // Still show mock data on error
        setCampaigns([]);
        setActivationOptions(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Update Emma's page state context whenever dashboard data changes
  useEffect(() => {
    const isFirstTime = counts.clients === 0 && counts.partners === 0 && counts.vendors === 0 && counts.solutions === 0;
    const visibleButtons: string[] = [];
    
    if (isFirstTime) {
      // MUST match actual button text exactly - see line 239
      visibleButtons.push('Start Setup (Step 1 of 3)');
    } else {
      visibleButtons.push('Create new campaign');
    }
    
    if (campaigns.length > 0) {
      visibleButtons.push('View Campaigns');
    }

    const userJourneyStage: 'onboarding' | 'returning' | 'active' = isFirstTime
      ? 'onboarding'
      : campaigns.length === 0
        ? 'returning'
        : 'active';

    const pageStateData = {
      isEmpty: campaigns.length === 0,
      entityCounts: {
        clients: counts.clients,
        partners: counts.partners,
        vendors: counts.vendors,
        solutions: counts.solutions,
      },
      campaignCount: campaigns.length,
      visibleButtons,
      primaryMessage: isFirstTime 
        ? 'Click the "Start Setup (Step 1 of 3)" button to begin setting up your ecosystem.'
        : campaigns.length === 0
        ? 'Get started by creating your first campaign'
        : `You have ${campaigns.length} campaign${campaigns.length !== 1 ? 's' : ''}`,
      userJourneyStage,
      errorState: error || undefined,
      dataLoading: isLoading,
    };

    // Update global store for UniversalEmmaChat to read
    console.log('[Dashboard] Setting Emma page state:', pageStateData);
    setEmmaPageState(pageStateData);
  }, [campaigns, counts, isLoading, error]);

  const handleCreateCampaign = () => {
    // First-time users (no entities) → Entity Manager to create foundation
    // Existing users → Campaign workflow
    const isFirstTime = counts.clients === 0 && counts.partners === 0 && counts.vendors === 0 && counts.solutions === 0;
    
    if (isFirstTime) {
      navigate('/data-foundation');
    } else {
      navigate('/campaigns/create');
    }
  };

  const isFirstTimeHome =
    counts.clients === 0 &&
    counts.partners === 0 &&
    counts.vendors === 0 &&
    counts.solutions === 0;

  const handleDeleteCampaign = async (id: string) => {
    try {
      await campaignService.deleteCampaign(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to delete campaign:', err);
    }
  };

  return (
    <div className="min-h-min bg-gradient-to-b from-emerald-100/45 via-emerald-50/25 to-slate-100/90">
      <div className="w-full px-3 py-4 pb-32 sm:px-4 sm:py-5 sm:pb-40">
        <header className="mb-5 rounded-2xl border border-emerald-200/50 bg-white/70 px-4 py-4 shadow-sm backdrop-blur-sm sm:px-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700/90">
                Workspace
              </p>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Home</h1>
              <div
                className="mt-2 h-0.5 w-12 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                aria-hidden
              />
            </div>
          </div>
          {Boolean(activationOptions?.activation_required) && (
            <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
              <p className="font-medium">Activation required before first generation.</p>
              {activationOptions?.can_manage_billing ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/settings/upgrade')}
                    className="h-8 border-indigo-300 text-indigo-800 hover:bg-indigo-100"
                  >
                    Choose a Plan
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/settings/billing')}
                    className="h-8 border-indigo-300 text-indigo-800 hover:bg-indigo-100"
                  >
                    Buy Credits or Pilot
                  </Button>
                </div>
              ) : (
                <p className="mt-1 text-indigo-800">
                  {activationOptions?.contact_admin_message || 'Contact your Org Admin to activate billing.'}
                </p>
              )}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-stretch gap-2 sm:items-center">
            {!isFirstTimeHome && (
              <Button
                variant="primary"
                onClick={() => navigate('/data-foundation')}
                disabled={isLoading}
                className="h-10 min-h-[2.5rem] w-full rounded-xl border-emerald-600 bg-emerald-600 px-4 text-sm font-medium text-white shadow-md hover:border-emerald-700 hover:bg-emerald-700 sm:w-auto sm:min-w-[200px]"
              >
                <Database className="h-3.5 w-3.5 text-white" />
                Manage entities
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleCreateCampaign}
              disabled={isLoading}
              className="h-10 min-h-[2.5rem] w-full gap-1.5 rounded-xl border-emerald-600 bg-emerald-600 px-4 text-sm font-medium text-white shadow-md hover:border-emerald-700 hover:bg-emerald-700 sm:w-auto sm:min-w-[200px]"
            >
              {isFirstTimeHome ? (
                <>
                  <Database className="h-3.5 w-3.5 text-white" />
                  Start Setup (Step 1 of 3)
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5 text-white" />
                  Create new campaign
                </>
              )}
            </Button>
          </div>
        </header>

        <HeroCTA
          clientCount={counts.clients}
          partnerCount={counts.partners}
          vendorCount={counts.vendors}
          solutionCount={counts.solutions}
          icpCount={counts.icps}
          documentCount={counts.documents}
          warnings={counts.warnings}
          lastUpdated={counts.last_updated}
          isLoading={isLoading}
        />

        {/* Phase 2: Credit balance + partner usage widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <CreditBalanceWidget />
          {hasPermission(user?.role as any, 'credit.view_usage') && (
            <PartnerUsageDashboard />
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-8 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-sm font-semibold text-red-900">Failed to load campaigns</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Recent campaigns */}
        <section
          className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-emerald-950/[0.04] sm:p-5"
          aria-labelledby="recent-campaigns-heading"
        >
          <div
            className="-mx-4 -mt-4 mb-4 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 sm:-mx-5 sm:-mt-5 sm:mb-5"
            aria-hidden
          />
          <div className="mb-4 flex flex-col gap-3">
            <div className="min-w-0">
              <div className="mb-0.5 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/90">
                  <LayoutGrid className="h-4 w-4" aria-hidden />
                </span>
                <h2 id="recent-campaigns-heading" className="text-base font-semibold text-slate-900 sm:text-lg">
                  Recent campaigns
                </h2>
              </div>
              <p className="text-xs text-slate-500 sm:text-sm">
                {campaigns.length === 0
                  ? 'Use the buttons above or open Workflow to get started.'
                  : `${filteredDashboardCampaigns.length} shown${
                      createdDateRange !== 'all' ? ' (filtered)' : ''
                    } · open a card to continue that campaign`}
              </p>
            </div>
            {campaigns.length > 0 && (
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Filter recent list by created date"
              >
                {(
                  [
                    { id: 'all' as const, label: 'All time' },
                    { id: 'today' as const, label: 'Today' },
                    { id: 'week' as const, label: 'Last 7 days' },
                    { id: 'month' as const, label: 'Last 30 days' },
                  ] as const
                ).map(({ id, label }) => {
                  const active = createdDateRange === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setCreatedDateRange(id)}
                      aria-pressed={active}
                      className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-all sm:px-3.5 sm:text-xs ${
                        active
                          ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                          : 'border-emerald-200 bg-white text-emerald-900 hover:border-emerald-400 hover:bg-emerald-50/80'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="mt-1 flex w-full max-w-md flex-shrink-0 flex-col gap-2 sm:flex-row sm:items-stretch">
              {campaigns.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/campaigns')}
                  className="group h-10 min-h-[2.5rem] w-full rounded-xl border-2 border-emerald-600/85 bg-white px-4 text-sm font-semibold text-emerald-900 shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-emerald-700 hover:bg-emerald-50 hover:shadow-md active:translate-y-0 sm:flex-1"
                >
                  All campaigns
                  <ArrowRight className="h-3.5 w-3.5 text-emerald-600 transition-transform duration-200 ease-out group-hover:translate-x-0.5" />
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/workflow')}
                className="group h-10 min-h-[2.5rem] w-full rounded-xl border-2 border-emerald-200 bg-white px-4 text-sm font-medium text-emerald-900 shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-emerald-400 hover:bg-emerald-50/80 hover:shadow-md active:translate-y-0 sm:flex-1"
              >
                <Sparkles className="h-3.5 w-3.5 text-emerald-600 transition-colors duration-200" />
                Workflow
              </Button>
            </div>
          </div>

          {campaigns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-emerald-200/80 bg-gradient-to-b from-emerald-50/40 to-slate-50/30 px-4 py-10 text-center transition-colors duration-200 hover:border-emerald-300 hover:from-emerald-50/60">
              <div className="group/icon mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-emerald-200/80 transition-all duration-200 ease-out hover:scale-[1.02] hover:ring-emerald-300 hover:shadow-md">
                <Plus className="h-5 w-5 text-emerald-600 transition-transform duration-200 ease-out group-hover/icon:scale-110" />
              </div>
              <h3 className="mb-1 text-sm font-semibold text-slate-900">No campaigns yet</h3>
              <p className="mx-auto mb-5 max-w-sm text-xs leading-relaxed text-slate-600">
                Open the workflow to build your first campaign.
              </p>
              <Button
                variant="primary"
                onClick={handleCreateCampaign}
                className="h-11 rounded-xl border-emerald-600 bg-emerald-600 px-6 text-sm font-semibold text-white shadow-md transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-emerald-700 hover:bg-emerald-700 hover:shadow-lg active:translate-y-0"
              >
                <Plus className="h-3.5 w-3.5 text-white" />
                {counts.clients === 0 &&
                counts.partners === 0 &&
                counts.vendors === 0 &&
                counts.solutions === 0
                  ? 'Start Setup (Step 1 of 3)'
                  : 'Create first campaign'}
              </Button>
            </div>
          ) : filteredDashboardCampaigns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-emerald-200/80 bg-emerald-50/30 px-4 py-8 text-center">
              <p className="text-sm font-medium text-slate-800">No campaigns in this period</p>
              <p className="mt-1 text-xs text-slate-600">
                Widen the filter or open <span className="font-medium text-emerald-800">All campaigns</span> for the
                full list.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreatedDateRange('all')}
                className="mt-4 h-9 rounded-xl border-2 border-emerald-600 px-4 text-xs font-semibold text-emerald-900 hover:bg-emerald-50"
              >
                Show all time
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {filteredDashboardCampaigns.map((campaign) => (
                <RecentCampaignCard
                  key={campaign.id}
                  id={campaign.id}
                  name={campaign.name}
                  clientName={campaign.clientName}
                  status={campaign.status}
                  createdAt={campaign.createdAt}
                  templateType={campaign.templateType}
                  onDelete={handleDeleteCampaign}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
